/* eslint-disable max-lines-per-function */
import {AjaxAddToCart} from 'extensions/AjaxAddToCart';
import {imageFallback} from 'extensions/ui-directives';
import {defineCustomElement} from 'extensions/ui-utils';
import {html, LitElement, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import stylesheet from './x-add-on-product.scss?as=stylesheet';

export class AddOnProduct extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			_imageLoaded: {
				state: true,
				type: Boolean
			},
			basePrice: {
				attribute: 'base-price',
				type: Number
			},
			formattedBasePrice: {
				attribute: 'formatted-base-price',
				type: String
			},
			formattedPrice: {
				attribute: 'formatted-price',
				type: String
			},
			gasLevy: {
				attribute: 'gas-levy',
				type: Number
			},
			imageHeight: {
				attribute: 'image-height',
				type: Number
			},
			imageUrl: {
				attribute: 'image-url',
				type: String
			},
			imageWidth: {
				attribute: 'image-width',
				type: Number
			},
			name: {
				type: 'String'
			},
			price: {
				type: Number
			},
			refrigerantType: {
				attribute: 'refrigerant-type',
				type: String
			},
			showAddToCart: {
				attribute: 'show-add-to-cart',
				type: Boolean
			},
			sku: {
				type: String
			},
			url: {
				type: String
			}
		};
	}

	constructor () {
		super();

		// Internal default values

		/**
		 * @type {boolean}
		 */
		this._imageLoaded = false;

		// Default values

		/**
		 * @type {number}
		 */
		this.basePrice = 0;

		/**
		 * @type {string}
		 */
		this.formattedBasePrice = '';

		/**
		 * @type {string}
		 */
		this.formattedPrice = '';

		/**
		 * @type {string}
		 */
		this.gasLevy = '';

		/**
		 * @type {boolean}
		 */
		this.imageHeight = 0;

		/**
		 * @type {string}
		 */
		this.imageUrl = '';

		/**
		 * @type {boolean}
		 */
		this.imageWidth = 0;

		/**
		 * @type {string}
		 */
		this.name = '';

		/**
		 * @type {boolean}
		 */
		this.price = 0;

		/**
		 * @type {'R32'|'R410'}
		 */
		this.refrigerantType = '';

		/**
		 * @type {boolean}
		 */
		this.showAddToCart = false;

		/**
		 * @type {string}
		 */
		this.sku = '';

		/**
		 * @type {string}
		 */
		this.url = '';
	}

	firstUpdated () {
		const purchaseForms = this.renderRoot.querySelectorAll('.js-product-purchase-form');
		for (const form of purchaseForms) {
			new AjaxAddToCart(form);
		}
	}

	handleImageLoad () {
		this._imageLoaded = true;
	}

	renderInner () {
		const boundingBoxClasses = {
			'bounding-box': true,
			'is--loaded': this._imageLoaded
		};

		return html`
			<figure class="${classMap(boundingBoxClasses)}">
				<img src="${this.imageUrl}" alt="${this.sku} image" loading="lazy" @load="${this.handleImageLoad}" ${imageFallback(window.mivaJS.Image_Not_Found_Grid)} />
			</figure>
			<div class="info">
				<div class="name">${this.name}</div>
				${this.renderPrice()}
			</div>
			${this.renderAddToCart()}
		`;
	}

	renderPrice () {
		if (!this.price) {
			return html`
			<div class="price-container">
				<span class="price price-hyphen"></span>
			</div>
		`;
		}

		return html`
			<div class="price-container">
				<span class="price">${this.formattedPrice}</span>
				${this.basePrice > this.price ? html`<s class="base-price">${this.formattedBasePrice}</s>` : nothing}
			</div>
		`;
	}

	renderAttributes () {
		return this.gasLevy
			? html`
				<input type="hidden" name="Product_Attributes[ 1 ]:code" value="GasLevy" />
				<input type="hidden" name="Product_Attributes[ 1 ]:value" value="${this.refrigerantType}" />
			`
			: nothing;
	}

	renderAddToCart () {
		if (!this.showAddToCart) {
			return nothing;
		}

		return html`
			<form class="js-product-purchase-form product-purchase-form" action="${window.mivaJS?.Basket_Url}" method="post">
				<input type="hidden" name="Action" value="ADPR" />
				${this.renderAttributes()}
				<input type="hidden" name="Product_Code" value="${this.sku}" />
				<input type="hidden" name="Product_Name" value="${this.name}" />
				<input type="hidden" name="Product_Price" value="${this.price}" />
				<input type="hidden" name="Quantity" value="1" />
				<button class="product-purchase-button js-add-to-cart" data-action="${window.mivaJS?.Basket_Url}" data-value="Add" type="submit">+ Add</button>
				<div class="js-add-to-cart-message" hidden></div>
			</form>
		`;
	}

	render () {
		return html`
			<a href="${this.url}" class="container">
				${this.renderInner()}
			</a>
		`;
	}
};

defineCustomElement('x-add-on-product', AddOnProduct);
